import styled, { DefaultTheme } from 'styled-components/macro';
import {
  ListCheckDetailHead,
  ListChecksLayout,
  ListChecksPanelContainer,
  ListCheckTitle
} from '../../styles';

export const Shell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  background: ${({ theme }) => theme.color.gray[100]};
`;

export const Body = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  padding: 16px;
  min-height: 0;
`;

export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
  min-width: 0;
  min-height: 0;
`;

export const Panes = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  min-height: 0;
`;

export const Region = styled.section`
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 8px;
  overflow-y: auto;
  min-width: 0;
`;

export const CheckRailRegion = styled(Region)`
  flex: 0 0 440px;
  display: flex;
  flex-direction: column;
`;

export const RailScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 160px;
  overflow-y: auto;
`;

export const RailFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};
  flex-shrink: 0;
`;

export const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const DocumentRegion = styled(Region)`
  flex: 1 1 0;
  overflow: hidden;
  display: flex;
  position: relative;
`;

export const DocumentSurface = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

export const ViewerControlsBelowHeader = styled.div`
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;

  > div {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
`;

export const ViewerSurface = styled.div<{ $hidden: boolean }>`
  width: 100%;
  height: 100%;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export const DocumentStatusMessage = styled.p`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const SwitcherBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const SwitcherHeading = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[700]};
`;

export const DocumentChipList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 6px;
  max-height: 62px;
  overflow-y: auto;
  padding: 6px 4px 0;
`;

export const DocumentChipBadge = styled.img`
  position: absolute;
  top: -6px;
  right: -4px;
  width: 14px;
  height: 14px;
`;

export const DocumentChip = styled.button<{ isCurrent: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 12px;
  font-size: 14px;
  cursor: pointer;
  background: ${({ theme, isCurrent }) => (isCurrent ? theme.color.blue[75] : theme.color.white)};
  color: ${({ theme }) => theme.color.gray[700]};
  border: 1px solid
    ${({ theme, isCurrent }) => (isCurrent ? theme.color.blue[600] : theme.color.gray[250])};

  &:hover {
    border-color: ${({ theme }) => theme.color.blue[600]};
  }
`;

export type AvChipTone = 'clean' | 'edited' | 'insufficient' | 'other';

export const AvDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 20px;
  margin: 0 2px;
  background: ${({ theme }) => theme.color.gray[300]};
`;

const avChipColors = (
  color: DefaultTheme['color'],
  tone: AvChipTone
): { border: string; text: string; tint: string } =>
  tone === 'clean'
    ? { border: color.green[700], text: color.green[700], tint: color.green[50] }
    : tone === 'edited'
    ? { border: color.red[700], text: color.red[700], tint: color.red[100] }
    : tone === 'insufficient'
    ? { border: color.yellow[700], text: color.yellow[800], tint: color.yellow[50] }
    : { border: color.gray[700], text: color.gray[700], tint: color.gray[100] };

export const AvDocumentChip = styled(DocumentChip)<{ tone: AvChipTone }>`
  font-weight: 600;
  color: ${({ theme, tone }) => avChipColors(theme.color, tone).text};
  background: ${({ theme, tone, isCurrent }) =>
    isCurrent ? avChipColors(theme.color, tone).tint : theme.color.white};
  border: ${({ isCurrent }) => (isCurrent ? 2 : 1)}px solid
    ${({ theme, tone }) => avChipColors(theme.color, tone).border};

  &:hover {
    border-color: ${({ theme, tone }) => avChipColors(theme.color, tone).border};
  }
`;

export const RailStatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  min-height: 40px;
`;

export const SidePanelRegion = styled(Region)`
  flex: 0 0 460px;
  display: flex;
  overflow: hidden;

  && > * {
    flex: 1;
    width: auto;
    min-width: 0;
    height: auto;
    border-left: none;
  }
`;

export const DocumentColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
`;

export const RailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  flex-shrink: 0;
`;

export const RailHeaderText = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const RailNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.red[700]};
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  flex-shrink: 0;
`;

export const RailApplicant = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[900]};
`;

export const RailProperty = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[600]};
`;

export const RailSections = styled.div`
  flex: 1;
  padding: 10px 0 14px;
  background-color: ${({ theme }) => theme.color.gray[50]};
`;

export const RailMutedText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const InspectorListChecks = styled(ListChecksPanelContainer)`
  padding-bottom: 0;
  background-color: ${({ theme }) => theme.color.white};

  & > ${ListChecksLayout} {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  ${ListCheckDetailHead} {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 75px;
    padding: 16px 20px;
  }

  ${ListCheckTitle} {
    font-size: 15px;
  }
`;
